import { MetaDescriptor } from "@remix-run/cloudflare";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { fallbackLng, supportedLngs } from '~/i18n';
import { BookingCart, BookingCartItem } from "~/stores/bookingCart";
import { YoastHeadJson } from "~/types/seo";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function cleanupUrl(url: string): string {
  const regex = new RegExp(`^/(${supportedLngs.join("|")})(/|$)`);

  return url.replace(regex, "/");
}

export function getMenuHref(currentLng: string, href: string) {
  if (currentLng === fallbackLng) {
    return href;
  }

  return `/${currentLng}${href}`;
}

export function formatDateToYYYYMMDD(date?: Date) {
  if (!date) return undefined
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function formatBookingDateWithWeekday(date: Date | string, lang?: string): string {
  const d = typeof date === "string" ? new Date(`${date}T00:00:00Z`) : date;
  if (!d || isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat(lang || "it", {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const weekday = get("weekday");
  const weekdayCapitalized =
    weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return `${weekdayCapitalized} ${get("day")}/${get("month")}/${get("year")}`;
}


export const calculateBookingCartSummary = (bookingCart: BookingCart) => {

  if (
    !bookingCart ||
    !bookingCart.BasketItems ||
    bookingCart.BasketItems.length === 0
  ) {
    return { total: 0, currency: "", totalGuests: 0 };
  }

  const totalGross = bookingCart?.BasketItems?.reduce((acc: number, item: BookingCartItem) => {
    if (item.Role === "INCLUDED") {
      return acc;
    }
    return acc + (item.GrossAmount?.Price || 0);
  }, 0);

  const totalNet = bookingCart.TotalNetAmount?.Price || totalGross;
  const currency = bookingCart.TotalNetAmount?.Currency || bookingCart.BasketItems[0].GrossAmount?.Currency || "";
  return { total: totalNet, currency };
};

export const mapCreditCardBrand = (stripeBrand?: string) => {

  switch (stripeBrand) {
    case "mastercard":
      return "MC";
    case "visa":
      return "VS";
    case "amex":
      return "AX";
    case "diners":
      return "DC";
    case "discover":
      return "DS";
    case "jcb":
      return "JCB";
    case "cartes_bancaires":
    case "eftpos_au":
    case "link":
    default:
      return "";
  }
}

const DEFAULT_META_TITLE_PREFIX = "Chorus Wellness | ";

export const createMeta = (pageTitle: string, pageDescription: string): MetaDescriptor[] => {
  return [
    {
      title: `${DEFAULT_META_TITLE_PREFIX}${pageTitle}`,
    },
    { name: "description", content: pageDescription }
  ]
}

export function getCmsServiceFromList(cmsServices: any[], serviceCode: string) {
  return cmsServices.find(s => s.acf.codes.find((c: any) => c.code === serviceCode));
}
export function getCmsServiceCategoryFromList(cmsServiceCategories: any[], serviceCode: string) {
  return cmsServiceCategories.find(s => s.acf.code === serviceCode);
}

export function getNormalServiceCategories(serviceCategories: any[]) {
  return serviceCategories.filter((category: any) => !category.acf?.is_an_upselling);
}

export function getUpsellingServiceCategories(serviceCategories: any[]) {
  return serviceCategories.filter((category: any) => category.acf?.is_an_upselling);
}

export function isEnabled(value: any): boolean {
  return value !== undefined && value !== null && value !== false && value !== "false";
}

export function validateItalianTaxCodeOrVAT(value: string): boolean {
  if (!value || value.trim().length === 0) {
    return false;
  }

  const cleaned = value.trim().toUpperCase().replace(/\s/g, "");

  if (cleaned.length === 11) {
    return validatePartitaIVA(cleaned);
  } else if (cleaned.length === 16) {
    return validateCodiceFiscale(cleaned);
  }

  return false;
}

export function isVATId(value: string): boolean {
  if (!value || value.trim().length === 0) {
    return false;
  }

  const cleaned = value.trim().toUpperCase().replace(/\s/g, "");

  if (cleaned.length === 11) {
    return validatePartitaIVA(cleaned);
  }

  return cleaned.length <= 20 && /^[A-Z0-9]+$/.test(cleaned);
}

export function isTaxCode(value: string): boolean {
  if (!value || value.trim().length === 0) {
    return false;
  }

  const cleaned = value.trim().toUpperCase().replace(/\s/g, "");

  if (cleaned.length === 16) {
    return validateCodiceFiscale(cleaned);
  }

  return false;
}

export function getInputType(value: string): "vat" | "taxcode" | "unknown" {
  if (!value || value.trim().length === 0) {
    return "unknown";
  }

  const cleaned = value.trim().toUpperCase().replace(/\s/g, "");

  if (cleaned.length === 11 && validatePartitaIVA(cleaned)) {
    return "vat";
  }

  if (cleaned.length === 16 && validateCodiceFiscale(cleaned)) {
    return "taxcode";
  }

  // if (cleaned.length <= 20 && /^[A-Z0-9]+$/.test(cleaned)) {
  //   return "vat";
  // }

  return "unknown";
}

const EU_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE"
];

export function isEUCountry(country: string): boolean {
  return EU_COUNTRIES.includes(country?.toUpperCase() || "");
}

function validatePartitaIVA(piva: string): boolean {
  if (!/^\d{11}$/.test(piva)) {
    return false;
  }

  let sum = 0;

  for (let i = 0; i <= 9; i += 2) {
    sum += piva.charCodeAt(i) - '0'.charCodeAt(0);
  }

  for (let i = 1; i <= 9; i += 2) {
    let c = 2 * (piva.charCodeAt(i) - '0'.charCodeAt(0));
    if (c > 9) {
      c = c - 9;
    }
    sum += c;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === (piva.charCodeAt(10) - '0'.charCodeAt(0));
}

function validateCodiceFiscale(cf: string): boolean {
  if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(cf)) {
    return false;
  }

  const evenMap: Record<string, number> = {
    "0": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9,
    "A": 0, "B": 1, "C": 2, "D": 3, "E": 4, "F": 5, "G": 6, "H": 7, "I": 8, "J": 9,
    "K": 10, "L": 11, "M": 12, "N": 13, "O": 14, "P": 15, "Q": 16, "R": 17, "S": 18,
    "T": 19, "U": 20, "V": 21, "W": 22, "X": 23, "Y": 24, "Z": 25
  };

  const oddMap: Record<string, number> = {
    "0": 1, "1": 0, "2": 5, "3": 7, "4": 9, "5": 13, "6": 15, "7": 17, "8": 19, "9": 21,
    "A": 1, "B": 0, "C": 5, "D": 7, "E": 9, "F": 13, "G": 15, "H": 17, "I": 19, "J": 21,
    "K": 2, "L": 4, "M": 18, "N": 20, "O": 11, "P": 3, "Q": 6, "R": 8, "S": 12, "T": 14,
    "U": 16, "V": 10, "W": 22, "X": 25, "Y": 24, "Z": 23
  };

  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const char = cf[i];
    if (i % 2 === 0) {
      sum += oddMap[char] || 0;
    } else {
      sum += evenMap[char] || 0;
    }
  }

  const remainder = sum % 26;
  const checkChar = String.fromCharCode(65 + remainder);
  return checkChar === cf[15];
}


export function getMetaData(data: any, fallback?: { title?: string, description?: string, robots?: string }) {
  const seo = (data?.seo ?? {}) as YoastHeadJson;
  const tags: MetaDescriptor[] = [];
  const lang = data?.lang ?? "it";
  const title = seo.title ?? (fallback?.title ? `ChorusLife Immersive SPA – ${fallback.title}` : "ChorusLife Immersive SPA – Centro benessere immersivo");
  const desc = seo.description ?? fallback?.description ?? "Scopri Chorus SPA: un’esperienza di benessere totale con percorsi sensoriali, massaggi rilassanti e trattamenti personalizzati. Vivi il tuo momento di relax.";

  if (title) tags.push({ title });
  if (desc) tags.push({ name: "description", content: desc });

  // `index, follow` stays the default for every page. A route opts out by
  // passing `robots` explicitly - used by pages that are published but must
  // not be discoverable (see `($locale).servizi.tsx`).
  tags.push({ name: "robots", content: fallback?.robots ?? ['index, follow'].join(", ") });

  if (seo.canonical)
    tags.push({
      tagName: "link",
      rel: "canonical",
      href: seo.canonical,
    } as any);

  // Open Graph
  if (seo.og_title) tags.push({ property: "og:title", content: seo.og_title });
  if (seo.og_description)
    tags.push({ property: "og:description", content: seo.og_description });
  if (seo.og_type) tags.push({ property: "og:type", content: seo.og_type });
  if (seo.og_locale) {
    const OG_LOCALE_MAP: Record<string, string> = { it: "it_IT", en: "en_US" };
    const ogLocale = OG_LOCALE_MAP[lang ?? "it"] || "it_IT";
    tags.push({ property: "og:locale", content: ogLocale });
  }
  if (seo.og_site_name)
    tags.push({ property: "og:site_name", content: seo.og_site_name });

  const ogImg = seo.og_image?.[0];
  if (ogImg?.url) {
    tags.push({ property: "og:image", content: ogImg.url });
    if (ogImg.width)
      tags.push({ property: "og:image:width", content: String(ogImg.width) });
    if (ogImg.height)
      tags.push({ property: "og:image:height", content: String(ogImg.height) });
    if (ogImg.type)
      tags.push({ property: "og:image:type", content: ogImg.type });
  }

  // Twitter
  tags.push({
    name: "twitter:card",
    content: seo.twitter_card || "summary_large_image",
  });
  if (seo.og_title) tags.push({ name: "twitter:title", content: seo.og_title });
  if (seo.og_description)
    tags.push({ name: "twitter:description", content: seo.og_description });
  if (ogImg?.url) tags.push({ name: "twitter:image", content: ogImg.url });

  return tags;
}

/**
 * Right-of-withdrawal (recesso) eligibility window.
 *
 * `maxDays` is configured in WordPress (ACF options page "App Settings",
 * field `recesso_max_days`). Returns `true` while the order is still inside
 * the allowed withdrawal window.
 *
 * Conservative-by-feature default: if `maxDays` is not a positive number
 * (field empty / unset / 0 / non-numeric) NO limit is applied and the previous
 * behaviour is kept (recesso always available). This avoids silently disabling
 * a working feature if the admin forgets to configure the field.
 *
 * Used both client-side (hide the "Richiedi recesso" button) and server-side
 * (reject an expired withdrawal request in the API action).
 */
export function isWithinWithdrawalWindow(
  orderDate: string | Date | null | undefined,
  maxDays: number | string | null | undefined
): boolean {
  const days = typeof maxDays === "string" ? parseInt(maxDays, 10) : maxDays;

  // No (valid) limit configured → no restriction.
  if (!days || !Number.isFinite(days) || days <= 0) return true;
  if (!orderDate) return true;

  const created = new Date(orderDate).getTime();
  if (Number.isNaN(created)) return true;

  const elapsedDays = Math.floor((Date.now() - created) / 86_400_000);
  return elapsedDays <= days;
}

/**
 * Reads gift card fields off a raw `vBookingCatalogGiftCard` row (see
 * app/routes/api.gift-cards.ts). The API is not consistent about casing, so
 * every reader here tries both (e.g. `Barcode` and `barcode`), exactly like
 * GiftCardGridItem.tsx used to do inline before these were extracted here.
 */
export function getGiftCardCode(giftCard: any): string {
  return giftCard?.Barcode ?? giftCard?.barcode ?? "";
}

export function getGiftCardBalance(giftCard: any): number {
  const rawBalance = giftCard?.Balance ?? giftCard?.balance;
  return typeof rawBalance === "number" ? rawBalance : parseFloat(rawBalance);
}

export function getGiftCardExpiryDate(giftCard: any): string | undefined {
  return giftCard?.ExpiryDate ?? giftCard?.expirydate;
}

export function getGiftCardIssueDate(giftCard: any): string | undefined {
  return giftCard?.IssueDate ?? giftCard?.issuedate;
}

// Calendar-date ordinal (YYYYMMDD as an integer) so two dates compare as
// days, not as timestamps. Shared by the gift card expiry helpers below.
const toYmd = (y: number, m: number, d: number) => y * 10000 + m * 100 + d;

// The customer's LOCAL today, as a calendar ordinal.
function localTodayYmd(): number {
  const now = new Date();
  return toYmd(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

// A gift card date (ExpiryDate/IssueDate are calendar dates, parsed as UTC
// midnight) as a calendar ordinal, or null when missing or unreadable.
function giftCardDateYmd(value?: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return toYmd(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

/**
 * A gift card is expired ON its expiry day: the last usable day is the day
 * before the expiry date, which is how the backend treats it (a card that
 * expires on 1 September can be spent until 31 August). Compares calendar
 * dates (the expiry, parsed as UTC midnight, against the customer's LOCAL
 * today) rather than timestamps, to avoid the off-by-one-day shift in
 * timezones behind UTC. Shared by the account tab card (GiftCardGridItem)
 * and the gift card picker in PaymentStep so both agree on what "expired"
 * means.
 */
export function isGiftCardExpired(expiryDate?: string | null): boolean {
  const expiryYmd = giftCardDateYmd(expiryDate);
  if (expiryYmd === null) return false;
  const todayYmd = localTodayYmd();
  return expiryYmd <= todayYmd;
}

/** Usable as an advance payment: not expired, and an actual positive balance. */
export function isGiftCardUsable(giftCard: any): boolean {
  const balance = getGiftCardBalance(giftCard);
  if (!Number.isFinite(balance) || balance <= 0) return false;
  return !isGiftCardExpired(getGiftCardExpiryDate(giftCard));
}

/**
 * Display-only masking for a gift card code, used where a customer picks
 * among their OWN cards (e.g. the gift card picker in PaymentStep). Not a
 * security boundary: app/routes/api.gift-cards.ts already returns the full
 * code so the account tab can render a scannable barcode from it - this only
 * avoids showing the full code in a plain list where it isn't needed.
 * Mirrors maskCardCode in app/lib/gift-card.server.ts (masking there is
 * security-relevant: it is what keeps a code applied to a reservation out of
 * client-visible/persisted state), duplicated here only because that file is
 * server-only and cannot be imported from client code.
 */
export function maskGiftCardCode(code: string): string {
  if (!code || code.length <= 4) return code;
  return `${"*".repeat(Math.max(0, code.length - 4))}${code.slice(-4)}`;
}

/**
 * How long an expired gift card keeps being listed in the account tab. The
 * customer asked for expired cards to drop off the list "after a while,
 * e.g. two months" (ClickUp CHLSPA-008). The payment picker is not affected:
 * it already hides every expired card (isGiftCardUsable).
 */
export const GIFT_CARD_HIDE_AFTER_EXPIRY_MONTHS = 2;

/**
 * True when the card expired more than GIFT_CARD_HIDE_AFTER_EXPIRY_MONTHS
 * calendar months ago, i.e. expiry + N months is today or earlier (a card
 * that expired on 7 July disappears on 7 September). Same calendar-date
 * comparison as isGiftCardExpired. Adding months clamps to the last day of
 * the target month (31 July + 2 months = 30 September), so a card is never
 * hidden a day early because JS rolled the date over into the next month.
 */
export function isGiftCardStale(expiryDate?: string | null): boolean {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) return false;

  const monthIndex =
    expiry.getUTCMonth() + GIFT_CARD_HIDE_AFTER_EXPIRY_MONTHS;
  const year = expiry.getUTCFullYear() + Math.floor(monthIndex / 12);
  const month = monthIndex % 12;
  // Day 0 of the following month is the last day of the target month.
  const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(expiry.getUTCDate(), lastDayOfMonth);

  return toYmd(year, month + 1, day) <= localTodayYmd();
}

/**
 * The customer's gift cards ordered by expiry date. `asc` puts the card
 * that expires first at the top (the one to spend first); `desc` puts the
 * most recently expired first. Cards without a readable expiry date never
 * expire (see isGiftCardExpired) and always sort last. Stable and pure:
 * ties keep the input order and the input array is not mutated.
 */
export function sortGiftCardsByExpiry(
  giftCards: any[],
  direction: "asc" | "desc" = "asc"
): any[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...giftCards].sort((a, b) => {
    const aYmd = giftCardDateYmd(getGiftCardExpiryDate(a));
    const bYmd = giftCardDateYmd(getGiftCardExpiryDate(b));
    if (aYmd === null && bYmd === null) return 0;
    if (aYmd === null) return 1;
    if (bYmd === null) return -1;
    return (aYmd - bYmd) * sign;
  });
}

export interface GiftCardBuckets {
  active: any[];
  expired: any[];
}

/**
 * Splits the customer's gift cards into the two lists the account tab shows
 * (ClickUp CHLSPA-006): active cards soonest-expiring first, expired cards
 * most recently expired first. "Expired" is isGiftCardExpired, the same rule
 * the backend applies (expired ON the expiry day). Pure: the input is not
 * mutated.
 */
export function splitGiftCardsByExpiry(giftCards: any[]): GiftCardBuckets {
  const active: any[] = [];
  const expired: any[] = [];
  for (const card of giftCards) {
    (isGiftCardExpired(getGiftCardExpiryDate(card)) ? expired : active).push(
      card
    );
  }
  return {
    active: sortGiftCardsByExpiry(active, "asc"),
    expired: sortGiftCardsByExpiry(expired, "desc"),
  };
}
